import { create } from 'zustand'
import { createAdapter, suggestAdapterKind } from '@/lib/printing/adapters'
import type { PrinterAdapter } from '@/lib/printing/types'

const STORAGE_KEY = 'octapos.printer'

interface PrinterState {
  kind: string
  adapter: PrinterAdapter
  isConnecting: boolean
  error: string | null

  choose: (kind: string) => void
  connect: () => Promise<void>
}

/**
 * The printer this device uses.
 *
 * Held per device in localStorage, not per user on the server: the same cashier moves between
 * a phone with a Bluetooth printer and a PC with a USB one, and the right answer belongs to
 * the hardware in front of them.
 */
export const usePrinter = create<PrinterState>((set, get) => {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY)
  const kind = stored ?? suggestAdapterKind()

  return {
    kind,
    adapter: createAdapter(kind),
    isConnecting: false,
    error: null,

    choose: (next) => {
      localStorage.setItem(STORAGE_KEY, next)
      set({ kind: next, adapter: createAdapter(next), error: null })
    },

    connect: async () => {
      set({ isConnecting: true, error: null })
      try {
        await get().adapter.connect()
      } catch (caught) {
        set({ error: caught instanceof Error ? caught.message : 'Could not connect to the printer.' })
      } finally {
        set({ isConnecting: false })
      }
    },
  }
})
